import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useLocation } from '@reach/router';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Button from '@components/shared/Button';
import Carousel from '@components/shared/Carousel';
import Colors from '@components/shared/Colors';
import FadeIn from '@components/shared/FadeIn';
import { BodyLG, Heading2Bold } from '@components/shared/TextTypes';
import { PageSlice, SliceLinkCarouselDetails, SliceLinkCarouselItem } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import useLinkButtonClick from '@utils/useLinkButtonClick';
import TextLinkButton from './TextLinkButton';

interface Props {
  slice: PageSlice;
}

const LinkCarousel = ({ slice }: Props) => {
  const { country } = useCountry();
  const location = useLocation();
  const sliceDetails = slice.primary as SliceLinkCarouselDetails;
  const sliceItems = slice.items as SliceLinkCarouselItem[];
  const onLinkButtonClick = useLinkButtonClick();
  const { t } = useTranslation();

  return (
    <FadeIn>
      <LinkCarouselWrap $background={sliceDetails.background?.text}>
        <CarouselWrap>
          <Carousel
            options={{
              setGallerySize: false,
              pageDots: true,
              prevNextButtons: true,
              cellAlign: 'center',
              watchCSS: false
            }}
          >
            {sliceItems.map((item, idx) => (
              <CarouselItem key={idx} $background={item.background.text} className="carousel-cell">
                <ItemImage>
                  <LazyLoadImage src={item.image.url} alt={item.image.alt ?? item.heading.text} />
                </ItemImage>
                <ItemContent>
                  <ItemHeading>{replaceText(t(item.heading.text), country, location)}</ItemHeading>
                  <ItemText>
                    <NormalText>{replaceText(t(item.subtitle.text), country, location)}</NormalText>
                    <MobileText>{replaceText(t(item.mobile_subtitle.text), country, location)}</MobileText>
                  </ItemText>
                  <ItemButton>
                    <NormalItemButton onClick={() => onLinkButtonClick(item.link?.text)}>
                      {t(item.link_text.text || 'components.learn-more')}
                    </NormalItemButton>
                    <MobileItemLink $color={Colors.navyBlue} onClick={() => onLinkButtonClick(item.link?.text)}>
                      {t(item.link_text.text || 'components.learn-more')}
                    </MobileItemLink>
                  </ItemButton>
                </ItemContent>
                <ItemBackground>
                  <GatsbyImage image={sliceDetails.background_image.gatsbyImageData} alt={sliceDetails.background_image.alt ?? ''} />
                </ItemBackground>
              </CarouselItem>
            ))}
          </Carousel>
        </CarouselWrap>
      </LinkCarouselWrap>
    </FadeIn>
  );
};

export default LinkCarousel;

const LinkCarouselWrap = styled.div<{ $background: string }>`
  background: ${(props) => (props.$background ? props.$background : Colors.cream)};
  padding-bottom: 50px;
  overflow: hidden;
`;

const CarouselWrap = styled.div`
  min-width: 360px;
  width: 1200px;
  margin: 0 auto;

  .flickity-prev-next-button {
    background: ${Colors.cream};
    border-radius: 100px !important;

    .flickity-button-icon {
      left: 38%;
      top: 36%;
      width: 11px;
      height: 11px;
    }

    &.next {
      right: 20px;
    }

    &.previous {
      left: 20px;
    }

    @media (max-width: 768px) {
      display: none;
    }
  }

  .flickity-page-dots {
    bottom: -32px;
  }

  .flickity-viewport {
    transition: height 0.2s;
  }

  .carousel {
    height: 500px;
  }

  .carousel-cell {
    height: 100%;
    margin-right: 20px;

    @media (max-width: 1200px) {
      margin-right: 15px;
    }
  }

  @media (max-width: 460px) {
    .carousel {
      height: 550px;
    }
  }

  @media (max-width: 440px) {
    .carousel {
      height: 580px;
    }
  }

  @media (max-width: 420px) {
    .carousel {
      height: 600px;
    }
  }

  @media (max-width: 1200px) {
    width: 100%;
    padding: 0 30px;

    .flickity-viewport {
      overflow: visible;
      transform: translateZ(0);
    }
  }
`;

const CarouselItem = styled.div<{ $background: string }>`
  height: 100%;
  width: 100%;
  background: ${(props) => (props.$background ? props.$background : Colors.babyBlue)};
  display: flex;
  border-radius: 20px;
  flex-direction: row;
  gap: 100px;
  justify-content: center;

  @media (max-width: 1200px) {
    padding: 0 25px;
    flex-direction: column;
    gap: 20px;
  }
`;

const ItemImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  img {
    height: 400px !important;
    object-fit: contain;
  }

  @media (max-width: 1200px) {
    justify-content: center;
    padding-top: 40px;
    img {
      height: 200px !important;
    }
  }
`;

const ItemContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  flex: 1;

  @media (max-width: 1200px) {
    gap: 15px;
    justify-content: flex-start;
  }
`;

const ItemHeading = styled(Heading2Bold)`
  color: ${Colors.navyBlue};
  text-align: left;
  padding-right: 120px;

  @media (max-width: 1200px) {
    text-align: center;
    padding: 0;
    font-weight: 600;
    font-size: 24px;
  }

  @media (max-width: 640px) {
    line-height: 110%;
  }
`;

const ItemText = styled.div``;

const NormalText = styled(BodyLG)`
  color: ${Colors.navyBlue};
  text-align: left;
  display: block;
  padding-right: 120px;

  @media (max-width: 1200px) {
    padding: 0 100px;
    text-align: center;
  }

  @media (max-width: 900px) {
    text-align: center;
    padding: 0 50px;
  }

  @media (max-width: 640px) {
    display: none;
  }
`;

const MobileText = styled(BodyLG)`
  color: ${Colors.navyBlue};
  text-align: center;
  display: none;

  @media (max-width: 640px) {
    display: block;
  }

  @media (max-width: 400px) {
    line-height: 120%;
  }
`;

const ItemButton = styled.div`
  @media (max-width: 1200px) {
    width: 100%;
    position: absolute;
    bottom: 40px;
  }
`;

const NormalItemButton = styled(Button)`
  width: 208px;
  margin-top: 5px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const MobileItemLink = styled(TextLinkButton)`
  margin: 0 auto 0 auto;
  display: none;

  @media (max-width: 1200px) {
    display: block;
  }
`;

const ItemBackground = styled.div`
  z-index: -1;
  position: absolute;
  bottom: 0;
  right: 0;

  @media (max-width: 1200px) {
    display: none;
  }
`;
